import React, { useEffect, useState } from "react";
import {
  getAIModelMastersList,
  deleteAIModelMaster,
} from "../../services/aiModelMastersService";
import { paginateData, calculateTotalPages } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import TableToolbar from "../Common/TableComponent/TableToolbar";
import TablesRow from "../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../Common/TableComponent/tableBadges";
import { Pagination } from "../Common/TableComponent/Pagination";
import { AddAIModel } from "./AddAIModel";
import { Loading } from "../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";

const getModelId = (item) => item.id ?? item.Id;

const getProviderType = (item) =>
  item.providerType || item.ProviderType || "-";

const getModelName = (item) => item.modelName || item.ModelName || "-";

const isDefaultModel = (item) => {
  const value = item.isDefault ?? item.IsDefault;
  return value === true || value === "true" || value === 1;
};

const renderIsDefaultBadge = (item) => {
  const isDefault = isDefaultModel(item);
  return (
    <span className={`badge badge-soft-${isDefault ? "success" : "secondary"}`}>
      {isDefault ? "Default" : "Non Default"}
    </span>
  );
};

export const ManageAIModelsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "ai-model-masters";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [modelsList, setModelsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAIModel, setSelectedAIModel] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    } else {
      console.log("No page access details found");
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAIModelMastersList();
      const list = response.data?.result || response.data || [];
      setModelsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = modelsList.filter((item) => {
    const provider = getProviderType(item).toLowerCase();
    const modelName = getModelName(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return provider.includes(query) || modelName.includes(query);
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (modelId) => {
    const confirmed = await confirmDelete("AI Model");
    if (confirmed) {
      try {
        await deleteAIModelMaster(modelId);
        setModelsList((prev) =>
          prev.filter((item) => getModelId(item) !== modelId)
        );
        Swal.fire(
          "Deleted!",
          "The AI model has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      {pageAccessDetails.addAccess ? (
        <AddAIModel
          editMode={editMode}
          initialData={selectedAIModel ?? {}}
          onSuccess={fetchData}
          setSelectedAIModel={setSelectedAIModel}
          setEditMode={setEditMode}
        />
      ) : (
        ""
      )}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage AI Model Masters</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Provider Type",
                          "Model Name",
                          "Is Default",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="6" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={getModelId(item)}
                              rowData={{
                                slNo:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                providerType: getProviderType(item),
                                modelName: getModelName(item),
                                isDefault: renderIsDefaultBadge(item),
                                status: renderStatusBadge(item),
                              }}
                              columns={[
                                "slNo",
                                "providerType",
                                "modelName",
                                "isDefault",
                                "status",
                              ]}
                              hideIcons={false}
                              onEdit={() => {
                                setSelectedAIModel(item);
                                setEditMode(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              onDelete={() => handleDelete(getModelId(item))}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
